import { FC } from 'react';
import {
	useGetProviderByIdQuery,
	useGetProviderFromLinguistLinkQuery,
	useUpdateProviderMutation,
} from '../../../../store/api/providers.api';
import { Avatar, Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { InHouse, Outsource, Verified, AvatarIcon } from '../../../_core/constants/icons.constants';
import { ProviderDto, UserRolesEnum, WorkStatusEnum, ServiceTypeStatusEnum } from '@families-link/shared';
import { useTranslation } from 'react-i18next';
import { useCurrentAccessTokenSelector } from '../../../auth';
import { skipToken } from '@reduxjs/toolkit/query';
import { FillEditIcon, InHouseIcon, OutsourceIcon, VerifiedIcon } from '../../../_core/components/icons';
import { useOpenModalForm } from '../../hooks/use-open-modal-form.hook';

interface ProvidersDetailsProps {
	providerId?: string;
	isLinguistLink?: boolean;
}

export const ProvidersDetails: FC<ProvidersDetailsProps> = ({ providerId, isLinguistLink }) => {
	const currentToken = useCurrentAccessTokenSelector();
	const isRequester = currentToken?.payload.sur === UserRolesEnum.Requester;

	const providerFromLinguistLink = useGetProviderFromLinguistLinkQuery((!providerId || !isLinguistLink) ? skipToken : { id: providerId });
	const providerById = useGetProviderByIdQuery((!providerId || isLinguistLink) ? skipToken : providerId);

	const { data: provider } = isLinguistLink
		? providerFromLinguistLink
		: providerById;
	const [t] = useTranslation();

	const [updateProvider] = useUpdateProviderMutation();
	const featchActionEdit = async (data: ProviderDto) => {
		const providerId = data?._id.toString();

		if (providerId) {
			await updateProvider({ id: providerId, updateData: data }).unwrap();
		}
	};

	const handleOpenModalEdit = useOpenModalForm({
		featchAction: featchActionEdit,
		isCreate: false,
		submitButtonName: t('providers.save-changes'),
		toastText: 'Provider updated successfully',
		formTitle: 'Edit Provider',
	});

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				padding: 3,
				gap: 3,
				overflowY: 'auto',
				overflowX: 'hidden',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					width: '100%',
					position: 'relative',
				}}
			>
				<Box>
					<Typography variant="h6" sx={{ fontWeight: 500, fontSize: '16px' }}>
						{t('providers.details')}
					</Typography>
				</Box>

				{!isLinguistLink && (
					<Box sx={{ position: 'absolute', right: 0, top: '-10px' }}>
						<Tooltip title={t('buttons.tooltips.edit')}>
							<IconButton
								color="primary"
								disabled={isRequester}
								onClick={() => {
									handleOpenModalEdit(provider);
								}}
							>
								<FillEditIcon sx={{ fontSize: 32 }} />
							</IconButton>
						</Tooltip>
					</Box>
				)}
			</Box>

			<Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
				<Box
					sx={{
						flexGrow: 1,
						display: 'flex',
						mr: 3,
					}}
				>
					{provider?.imageUrl ? (
						<Avatar
							src={provider?.imageUrl}
							alt={provider?.fullName}
							style={{ width: 98, height: 98, borderRadius: '32px' }}
						/>
					) : (
						<Box
							style={{
								width: 98,
								height: 98,
								borderRadius: '32px',
								backgroundColor: 'var(--blue-8-opacity, rgba(36, 57, 106, 0.08))',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<AvatarIcon />
						</Box>
					)}
				</Box>

				<Box
					sx={{
						width: '100%',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						gap: 2,
					}}
				>
					<Grid container sx={{ flexWrap: 'wrap' }}>
						<Grid item xs={4}>
							<Typography
								variant="body2"
								sx={{ fontWeight: 'regular', color: (theme) => theme.colors.grayLightBlue, fontSize: '12px' }}
							>
								{t('providers.full-name')}
							</Typography>
							<Typography sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{provider?.fullName}
							</Typography>
						</Grid>
						<Grid item xs={4}>
							<Typography
								variant="body2"
								sx={{ fontWeight: 'regular', color: (theme) => theme.colors.grayLightBlue, fontSize: '12px' }}
							>
								{t('providers.type')}
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<Typography component="span" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
									{provider?.verificationType ? t('providers.verified') : t('providers.no-verified')}{' '}
								</Typography>
								{provider?.verificationType ? <VerifiedIcon sx={{ fontSize: '18px', marginLeft: '8px' }} /> : null}
							</Box>
						</Grid>
						<Grid item xs={4}>
							<Typography
								variant="body2"
								sx={{ fontWeight: 'regular', color: (theme) => theme.colors.grayLightBlue, fontSize: '12px' }}
							>
								{t('providers.service-type')}
							</Typography>
							<Typography sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{provider?.serviceType === ServiceTypeStatusEnum.Appointment
									? t('providers.appointment')
									: provider?.serviceType === ServiceTypeStatusEnum.Project
									? t('providers.project')
									: provider?.serviceType === ServiceTypeStatusEnum.Both
									? `${t('providers.appointment')}, ${t('providers.project')}`
									: ''}
							</Typography>
						</Grid>
					</Grid>
					<Grid container sx={{ flexWrap: 'wrap' }}>
						<Grid item xs={4}>
							<Typography
								variant="body2"
								sx={{ fontWeight: 'regular', color: (theme) => theme.colors.grayLightBlue, fontSize: '12px' }}
							>
								{t('providers.target-lang')}
							</Typography>
							<Tooltip title={provider?.targetLanguage?.join(', ')}>
								<Box
									sx={{
										fontWeight: 500,
										display: '-webkit-box',
										WebkitBoxOrient: 'vertical',
										WebkitLineClamp: 3,
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										lineHeight: 1.4,
										maxHeight: 'calc(1.4em * 3)',
										wordBreak: 'break-word',
										whiteSpace: 'normal',
									}}
								>
									{provider?.targetLanguage?.join(', ')}
								</Box>
							</Tooltip>
						</Grid>
						<Grid item xs={4}>
							<Typography
								variant="body2"
								sx={{ fontWeight: 'regular', color: (theme) => theme.colors.grayLightBlue, fontSize: '12px' }}
							>
								{t('providers.status')}
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								{provider?.workStatus === WorkStatusEnum.InHouse ? (
									<InHouseIcon sx={{ fontSize: '12px', mr: '8px' }} />
								) : (
									<OutsourceIcon sx={{ fontSize: '12px', mr: '8px' }} />
								)}
								<Typography component="span" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
									{provider?.workStatus ? t('providers.work-in-house') : t('providers.work-outsource')}{' '}
								</Typography>
							</Box>
						</Grid>
						<Grid item xs={4}>
							<Typography
								variant="body2"
								sx={{ fontWeight: 'regular', color: (theme) => theme.colors.grayLightBlue, fontSize: '12px' }}
							>
								{t('providers.email')}
							</Typography>
							<Typography sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{provider?.email}
							</Typography>
						</Grid>
					</Grid>
				</Box>
			</Box>
		</Box>
	);
};
